/** Pure calculators for CRM underwriting tools (multifamily / value-add). */

export type IncreasedValueInputs = {
  units: number;
  rentIncreaseMo: number;
  occupancyPct: number;
  capRatePct: number;
};

export function calcIncreasedValue(i: IncreasedValueInputs): {
  annualRentIncrease: number;
  propertyValueIncrease: number;
} {
  const occ = Math.min(100, Math.max(0, i.occupancyPct)) / 100;
  const cap = Math.min(100, Math.max(0, i.capRatePct)) / 100;
  const annualRentIncrease = i.units * i.rentIncreaseMo * occ * 12;
  const propertyValueIncrease = cap > 0 ? annualRentIncrease / cap : 0;
  return { annualRentIncrease, propertyValueIncrease };
}

export type UnderwritingInputs = {
  units: number;
  rentPerUnitMo: number;
  otherIncomePct: number;
  occupancyPct: number;
  expensesPct: number;
  capRatePct: number;
};

export function calcSimpleUnderwriting(i: UnderwritingInputs): {
  annualIncome: number;
  otherIncome: number;
  grossIncome: number;
  expenses: number;
  noi: number;
  offerPrice: number;
} {
  const occ = Math.min(100, Math.max(0, i.occupancyPct)) / 100;
  const annualIncome = i.units * i.rentPerUnitMo * 12 * occ;
  const otherIncome = annualIncome * (Math.min(100, Math.max(0, i.otherIncomePct)) / 100);
  const grossIncome = annualIncome + otherIncome;
  const expenses = grossIncome * (Math.min(100, Math.max(0, i.expensesPct)) / 100);
  const noi = grossIncome - expenses;
  const cap = Math.min(100, Math.max(0.01, i.capRatePct)) / 100;
  const offerPrice = noi / cap;
  return { annualIncome, otherIncome, grossIncome, expenses, noi, offerPrice };
}

/** Monthly P&I; rate as annual percent (e.g. 6.5). */
export function monthlyMortgagePayment(
  principal: number,
  annualRatePct: number,
  amortYears: number
): number {
  if (principal <= 0 || amortYears <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  const n = Math.round(amortYears * 12);
  if (n <= 0) return 0;
  if (r === 0) return principal / n;
  const factor = Math.pow(1 + r, n);
  return (principal * r * factor) / (factor - 1);
}

export type DebtInputs = {
  purchasePrice: number;
  units: number;
  grossRentalIncomeYr: number;
  vacancyPct: number;
  otherIncomeYr: number;
  operatingExpensesYr: number;
  ltvPct: number;
  interestRatePct: number;
  amortYears: number;
};

export function calcDebt(i: DebtInputs): {
  effectiveGrossIncome: number;
  noi: number;
  loanAmount: number;
  equityRequired: number;
  annualDebtService: number;
  monthlyPayment: number;
  dscr: number;
  stabilizedCapRatePct: number;
  breakevenOccupancyPct: number;
  pricePerUnit: number;
} {
  const vac = Math.min(100, Math.max(0, i.vacancyPct)) / 100;
  const effectiveGrossIncome = i.grossRentalIncomeYr * (1 - vac) + i.otherIncomeYr;
  const noi = effectiveGrossIncome - i.operatingExpensesYr;
  const ltv = Math.min(100, Math.max(0, i.ltvPct)) / 100;
  const loanAmount = i.purchasePrice * ltv;
  const equityRequired = Math.max(0, i.purchasePrice - loanAmount);
  const monthlyPayment = monthlyMortgagePayment(
    loanAmount,
    i.interestRatePct,
    Math.max(1, i.amortYears)
  );
  const annualDebtService = monthlyPayment * 12;
  const dscr = annualDebtService > 0 ? noi / annualDebtService : 0;
  const stabilizedCapRatePct =
    i.purchasePrice > 0 ? (noi / i.purchasePrice) * 100 : 0;
  const breakevenOccupancyPct =
    i.grossRentalIncomeYr > 0
      ? ((i.operatingExpensesYr + annualDebtService) / i.grossRentalIncomeYr) * 100
      : 0;
  const pricePerUnit = i.units > 0 ? i.purchasePrice / i.units : 0;
  return {
    effectiveGrossIncome,
    noi,
    loanAmount,
    equityRequired,
    annualDebtService,
    monthlyPayment,
    dscr,
    stabilizedCapRatePct,
    breakevenOccupancyPct,
    pricePerUnit,
  };
}

export type EquityInputs = {
  totalProjectCost: number;
  loanAmount: number;
  holdingYears: number;
  annualNoi: number;
  exitCapRatePct: number;
  annualLoanPayment: number;
  gpEquityPct: number /** 0–100, GP share of equity */;
  preferredReturnLpPct: number;
  gpPromotePct: number /** promote on residual */;
};

function exitValueFromNoi(noi: number, exitCapPct: number): number {
  const c = Math.min(100, Math.max(0.01, exitCapPct)) / 100;
  return noi / c;
}

/**
 * Illustrative equity waterfall: return of capital, pref, promote split on residual to equity.
 * Does not model loan paydown precisely; uses user-provided annual loan payment for cash flow hints only.
 */
export function calcEquityStructure(i: EquityInputs): {
  totalEquity: number;
  gpEquity: number;
  lpEquity: number;
  loanToCostPct: number;
  annualCashFlowToEquity: number;
  exitValue: number;
  exitProceedsToEquity: number;
  equityMultiple: number;
} {
  const totalEquity = Math.max(0, i.totalProjectCost - i.loanAmount);
  const gpShare = Math.min(100, Math.max(0, i.gpEquityPct)) / 100;
  const gpEquity = totalEquity * gpShare;
  const lpEquity = totalEquity * (1 - gpShare);
  const loanToCostPct =
    i.totalProjectCost > 0 ? (i.loanAmount / i.totalProjectCost) * 100 : 0;
  const annualCashFlowToEquity = Math.max(0, i.annualNoi - i.annualLoanPayment);

  const exitValue = exitValueFromNoi(i.annualNoi, i.exitCapRatePct);
  /** Simplified: equity receives exit value minus outstanding debt proxy (loan amount). */
  const exitProceedsToEquity = Math.max(0, exitValue - i.loanAmount);
  const holding = Math.max(0, i.holdingYears);
  const prefTotal = lpEquity * (i.preferredReturnLpPct / 100) * holding;
  const afterPref = Math.max(0, exitProceedsToEquity - lpEquity - gpEquity - prefTotal);
  const promote = afterPref * (i.gpPromotePct / 100);
  const residual = Math.max(0, afterPref - promote);
  const gpTotalReturn = gpEquity + promote + residual * gpShare + annualCashFlowToEquity * holding * gpShare;
  const lpTotalReturn = lpEquity + prefTotal + residual * (1 - gpShare);
  const equityMultiple =
    totalEquity > 0 ? (gpTotalReturn + lpTotalReturn) / totalEquity : 0;

  return {
    totalEquity,
    gpEquity,
    lpEquity,
    loanToCostPct,
    annualCashFlowToEquity,
    exitValue,
    exitProceedsToEquity,
    equityMultiple,
  };
}
